import {partition} from '@benchling/js-utils';
import React from 'react';

import {
  PlateDataParameter,
  PlateMaterialInput,
  PlateProcedureParameterType,
} from 'samples/plate-maps/PlateMapConstants';
import MultiSelectSearchableDropdown from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';

export const THIS_STEP_HEADERS_BY_PARAMETER_TYPE: {[parameterType in PlateProcedureParameterType]: string} = {
  [PlateProcedureParameterType.DATA_PARAMETER]: 'Parameters in this step',
  [PlateProcedureParameterType.MATERIAL_INPUT]: 'Material inputs in this step',
};

export const OTHER_STEP_HEADERS_BY_PARAMETER_TYPE: {[parameterType in PlateProcedureParameterType]: string} =
  {
    [PlateProcedureParameterType.DATA_PARAMETER]: 'Other parameters',
    [PlateProcedureParameterType.MATERIAL_INPUT]: 'Material inputs in other steps',
  };

export type SearchablePlateProcedureParameterDropdownProps<
  TParameter extends PlateDataParameter | PlateMaterialInput,
> = {
  /** A list of plate-used procedure parameters to search through & select */
  parameters: Array<TParameter>;
  /** Message for when none are selected */
  noneSelectedMessage: React.ReactNode;
  /** Whether or not to close on inside click */
  closeOnInsideClick: boolean;
  /** A function to be called when a parameter is selected or deselected */
  onSelect: (selectedValues: Array<string>) => void;
  /** The list of versionId of parameters that are currently selected */
  selectedValues: Array<string>;
  /** Function to get OptionData from parameters */
  getOptionData: (parameter: TParameter) => OptionData;
  /** Override the default dropdown toggle element */
  dropdownToggleOverride?: JSX.Element;
  /** Whether the dropdown should be disabled */
  disabled: boolean;
  /** Optional custom option rendering */
  renderOptionContent?: (optionData: OptionData, shouldFade?: boolean) => JSX.Element;
} & (
  | {
      /** Can be controlled by the parent; otherwise, open state is changed from a click toggle */
      isOpen: boolean;
      /** Required if isOpen is controlled */
      onToggle: (isOpen: boolean) => void;
    }
  | {
      isOpen?: never;
      onToggle?: never;
    }
);

export default function SearchablePlateProcedureParameterDropdown<
  TParameter extends PlateDataParameter | PlateMaterialInput,
>({
  parameters,
  noneSelectedMessage,
  closeOnInsideClick,
  onSelect,
  selectedValues,
  getOptionData,
  dropdownToggleOverride,
  disabled,
  renderOptionContent,
  onToggle,
  isOpen,
}: SearchablePlateProcedureParameterDropdownProps<TParameter>): JSX.Element {
  const parameterType = parameters[0].type; // should all be the same type in the array
  // Only allow in context data parameters to be selected, unless they are already selected.
  const selectableParameters = parameters.filter(
    (parameter) => !(parameter.outOfContext ?? false) || selectedValues.includes(parameter.versionId)
  );
  const [thisStepParameters, otherParameters] = partition<TParameter, TParameter>(
    selectableParameters,
    (parameter) => parameter.inStep
  );
  const thisStepParameterIds = new Set(thisStepParameters.map((parameter) => parameter.versionId));
  const thisStepHeader = {name: THIS_STEP_HEADERS_BY_PARAMETER_TYPE[parameterType], isHeader: true};
  const otherStepHeader = {name: OTHER_STEP_HEADERS_BY_PARAMETER_TYPE[parameterType], isHeader: true};
  // Headers need to be passed as option data initially to properly register as hover targets.
  const optionDatas: Array<OptionData> = [
    ...(thisStepParameters.length > 0 ? [thisStepHeader] : []),
    ...thisStepParameters.map((p) => getOptionData(p)),
    ...(otherParameters.length > 0 ? [otherStepHeader] : []),
    ...otherParameters.map((p) => getOptionData(p)),
  ];
  return (
    <MultiSelectSearchableDropdown
      closeOnInsideClick={closeOnInsideClick}
      closeOnOutsideClick={true}
      noneSelectedMessage={noneSelectedMessage}
      dataTestAttrs={{component: 'SearchablePlateProcedureParameterDropdown', element: 'root'}}
      disabled={disabled}
      dropdownToggleOverride={dropdownToggleOverride}
      isOpen={isOpen}
      menuProps={{
        width: Menu.WIDTHS.FIT_CONTENT,
        getMatchingOptionDatas: (query: string, optionDatas: Array<OptionData>) => {
          const matchingOptionDatas = getMatchingOptionDatas(query, optionDatas);
          // Short circuit if we don't match anything.
          if (matchingOptionDatas.length === 0) {
            return matchingOptionDatas;
          }
          const [thisStepOptionDatas, otherStepOptionDatas] = partition<OptionData, OptionData>(
            matchingOptionDatas.filter((optionData) => !optionData.isHeader),
            (matchingOptionData) => thisStepParameterIds.has(matchingOptionData.value)
          );
          // Show headers if there are any matching option datas in that group
          return [
            ...(thisStepOptionDatas.length > 0 ? [thisStepHeader] : []),
            ...thisStepOptionDatas,
            ...(otherStepOptionDatas.length > 0 ? [otherStepHeader] : []),
            ...otherStepOptionDatas,
          ];
        },
        renderOptionContent,
      }}
      onSelect={onSelect}
      onToggle={onToggle}
      optionDatas={optionDatas}
      selectedValues={selectedValues}
    />
  );
}
